import { apiPost, apiPut, apiDelete, ApiError } from './apiClient';
import { ResponseDTO, MultipleAppTag, MultipleDeviceTag, MultiplePolicyTag, AppTag, DeviceTagAssignment, PolicyTag } from '@/types/api';
import {
  recordReconcileRun,
  type ReconcileCall,
  type ReconcileLogEntry,
} from './tagReconcileDebugLog';
import { recordPppcActivity } from './pppcActivityLog';
import { getCurrentUserEmail } from '@/lib/auth';

// --- App Tags ---

export async function assignAppTags(data: MultipleAppTag): Promise<string> {
  const res = await apiPost<ResponseDTO<null>>('/api/v1/tag/app-tag', data);
  return res.message || 'Tags assigned';
}

export async function updateAppTags(data: MultipleAppTag): Promise<string> {
  const res = await apiPut<ResponseDTO<null>>('/api/v1/tag/app-tag', data);
  return res.message || 'Tags updated';
}

export async function unassignAppTags(data: AppTag): Promise<string> {
  const res = await apiDelete<ResponseDTO<null>>('/api/v1/tag/app-tag', data);
  return res.message || 'Tags removed';
}

// --- Device Tags ---

export async function assignDeviceTags(data: MultipleDeviceTag): Promise<string> {
  const res = await apiPost<ResponseDTO<null>>('/api/v1/tag/device-tag', data);
  return res.message || 'Tags assigned';
}

export async function updateDeviceTags(data: MultipleDeviceTag): Promise<string> {
  const res = await apiPut<ResponseDTO<null>>('/api/v1/tag/device-tag', data);
  return res.message || 'Tags updated';
}

export async function unassignDeviceTags(data: DeviceTagAssignment): Promise<string> {
  const res = await apiDelete<ResponseDTO<null>>('/api/v1/tag/device-tag', data);
  return res.message || 'Tags removed';
}

// --- Policy Tags ---

export async function assignPolicyTags(data: MultiplePolicyTag): Promise<string> {
  const res = await apiPost<ResponseDTO<null>>('/api/v1/tag/policy-tag', data);
  return res.message || 'Tags assigned';
}

export async function updatePolicyTags(data: MultiplePolicyTag): Promise<string> {
  const res = await apiPut<ResponseDTO<null>>('/api/v1/tag/policy-tag', data);
  return res.message || 'Tags updated';
}

export async function unassignPolicyTags(data: PolicyTag): Promise<string> {
  const res = await apiDelete<ResponseDTO<null>>('/api/v1/tag/policy-tag', data);
  return res.message || 'Tags removed';
}

// --- Shared reconciliation ---

/**
 * True when the backend rejected an assign call because the (policy, tag)
 * pair already exists. Treated as success by reconcilePolicyTags so the
 * overall save is not aborted by a redundant association.
 */
export function isDuplicatePolicyTagAssignmentError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const code = error instanceof ApiError ? error.errorCode : "";
  return /duplicate|already exists|unique/i.test(`${message} ${code}`);
}

/**
 * Reconcile the tag set on a policy: assigns newly added ids and removes
 * dropped ones. Used by both App Config and PPPC save flows so the two
 * wizards have identical assignment semantics. Tag removal is best-effort
 * (stale UI state shouldn't fail a save); duplicate-add errors are swallowed.
 *
 * Every run is recorded to the tag-reconcile debug log (policy id, deltas,
 * actor, per-call endpoint+payload+outcome) so a "deployment did not occur"
 * report can be diagnosed after the fact without reproducing the failure.
 *
 * Audit trail: when the caller declares `audit.kind === "pppc"`, a `tagged`
 * entry is appended to the PPPC activity log iff the bound tag set actually
 * changed (added.length + removed.length > 0). The audit write is
 * best-effort and never blocks the deploy.
 */
export interface ReconcilePolicyTagsAudit {
  /** Domain of the policy being reconciled. Drives which audit log gets the entry. */
  kind: "pppc";
  /** Bundle id the policy targets — surfaces in per-app activity views. */
  bundleId?: string;
  /** Friendly app name used in the entry summary. */
  appName?: string;
  /**
   * True when the deploy reused an existing PPPC policy id (edit/duplicate
   * flow) rather than creating a new one. Surfaced in the `tagged` entry so
   * the audit timeline can distinguish "scope change on existing policy"
   * from "first deploy of a brand-new policy".
   */
  reusedExistingPolicy?: boolean;
}

export async function reconcilePolicyTags(args: {
  policyId: string;
  previousTagIds: string[];
  nextTagIds: string[];
  actor: string;
  /** Optional caller hint surfaced in the debug log (e.g. "useSavePppcPolicy"). */
  source?: string;
  /** Optional domain context that drives which audit log gets the entry. */
  audit?: ReconcilePolicyTagsAudit;
}): Promise<void> {
  const { policyId, previousTagIds, nextTagIds, actor, source, audit } = args;
  const prev = new Set(previousTagIds);
  const next = new Set(nextTagIds);
  const toAdd = [...next].filter((id) => !prev.has(id));
  const toRemove = [...prev].filter((id) => !next.has(id));

  const calls: ReconcileCall[] = [];
  const runStart = Date.now();
  let thrown: unknown;

  if (toAdd.length > 0) {
    const payload = {
      policy_ids: [policyId],
      tag_ids: toAdd,
      created_by: actor,
      updated_by: actor,
    };
    const callStart = Date.now();
    try {
      await assignPolicyTags(payload);
      calls.push({
        method: "POST",
        path: "/api/v1/tag/policy-tag",
        payload,
        outcome: "success",
        durationMs: Date.now() - callStart,
      });
    } catch (error) {
      const dup = isDuplicatePolicyTagAssignmentError(error);
      calls.push({
        method: "POST",
        path: "/api/v1/tag/policy-tag",
        payload,
        outcome: dup ? "duplicate-ignored" : "error",
        errorMessage: error instanceof Error ? error.message : String(error),
        errorCode: error instanceof ApiError ? error.errorCode : undefined,
        durationMs: Date.now() - callStart,
      });
      if (!dup) thrown = error;
    }
  }
  if (!thrown) {
    for (const tagId of toRemove) {
      const payload = {
        policy_id: policyId,
        tag_ids: [tagId],
        created_by: actor,
        updated_by: actor,
      };
      const callStart = Date.now();
      try {
        await unassignPolicyTags(payload);
        calls.push({
          method: "DELETE",
          path: "/api/v1/tag/policy-tag",
          payload,
          outcome: "success",
          durationMs: Date.now() - callStart,
        });
      } catch (error) {
        // Best-effort: don't block save on stale tag removal failures.
        calls.push({
          method: "DELETE",
          path: "/api/v1/tag/policy-tag",
          payload,
          outcome: "error",
          errorMessage: error instanceof Error ? error.message : String(error),
          errorCode: error instanceof ApiError ? error.errorCode : undefined,
          durationMs: Date.now() - callStart,
        });
      }
    }
  }

  const aggregate: ReconcileLogEntry["outcome"] = (() => {
    if (thrown) return "error";
    if (calls.length === 0) return "noop";
    if (calls.some((c) => c.outcome === "error")) return "partial";
    return "success";
  })();

  // Best-effort: never let a debug-log write break the deploy.
  try {
    recordReconcileRun({
      source,
      policyId,
      actor,
      previousTagIds: [...previousTagIds],
      nextTagIds: [...nextTagIds],
      added: toAdd,
      removed: toRemove,
      calls,
      outcome: aggregate,
      totalDurationMs: Date.now() - runStart,
    });
  } catch {
    /* noop */
  }

  // ---- Domain audit-log write (best-effort) ----------------------------
  // Only emit a `tagged` entry when the tag set actually changed AND the
  // overall reconcile didn't throw — i.e. the deployment scope materially
  // moved. Read failures or removal-only failures still log; an "error"
  // outcome for the add does not (the caller surfaces the error and will
  // typically retry, at which point we'll log on success).
  if (audit?.kind === "pppc" && (toAdd.length > 0 || toRemove.length > 0) && !thrown) {
    try {
      const parts: string[] = [];
      if (toAdd.length) parts.push(`+${toAdd.length} tag${toAdd.length === 1 ? "" : "s"}`);
      if (toRemove.length) parts.push(`-${toRemove.length} tag${toRemove.length === 1 ? "" : "s"}`);
      recordPppcActivity({
        policyId,
        action: "tagged",
        actor: getCurrentUserEmail() ?? actor,
        bundleId: audit.bundleId,
        tagIds: [...nextTagIds],
        previousTagIds: [...previousTagIds],
        summary: `${audit.appName || "PPPC policy"} — ${parts.join(", ")}`,
        details: {
          added: toAdd,
          removed: toRemove,
          total: nextTagIds.length,
          outcome: aggregate,
          reusedExistingPolicy: audit.reusedExistingPolicy ?? false,
          previousTagIds: [...previousTagIds],
          tagIds: [...nextTagIds],
          tagDelta: { added: toAdd, removed: toRemove, total: nextTagIds.length },
        },
      });
    } catch {
      /* noop */
    }
  }

  if (thrown) throw thrown;
}
